import { useEffect, useState } from 'react';
import { Button, Text } from '@gravity-ui/uikit';

/**
 * Установка на телефон.
 *
 * Android Chrome сам присылает `beforeinstallprompt`, но прячет его в меню.
 * Кнопка ловит это событие и ставит приложение в один тап. iPhone такого
 * события не шлёт: Safari ставит только из «Поделиться → На экран Домой»,
 * поэтому на iOS показываем этот шаг, пока человек сам не закроет подсказку.
 * Уже установленное приложение (`display-mode: standalone`) подсказку не видит.
 */

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const DISMISS_KEY = 'odonix.admin.pwa.dismissed';

function standalone(): boolean {
  return window.matchMedia('(display-mode: standalone)').matches || navigator.standalone === true;
}

function iosBrowser(): boolean {
  const ua = navigator.userAgent;
  return /iPhone|iPad|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

export function InstallApp() {
  const [prompt, setPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (standalone()) return;

    const onPrompt = (event: Event) => {
      event.preventDefault();
      setPrompt(event as BeforeInstallPromptEvent);
    };
    window.addEventListener('beforeinstallprompt', onPrompt);
    const onInstalled = () => {
      setPrompt(null);
      setIosHint(false);
    };
    window.addEventListener('appinstalled', onInstalled);

    if (iosBrowser() && window.localStorage.getItem(DISMISS_KEY) !== '1') setIosHint(true);

    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  if (prompt === null && !iosHint) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-30 flex items-center justify-between gap-3 border-t border-[var(--g-color-line-generic)] bg-[var(--g-color-base-float)] px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
      <Text variant="body-1">
        {prompt === null
          ? 'Поставьте админку на экран: «Поделиться», затем «На экран Домой».'
          : 'Админка откроется с экрана телефона, без адресной строки.'}
      </Text>
      {prompt === null ? (
        <Button
          view="flat"
          onClick={() => {
            window.localStorage.setItem(DISMISS_KEY, '1');
            setIosHint(false);
          }}
        >
          Закрыть
        </Button>
      ) : (
        <Button
          view="action"
          onClick={() => {
            void prompt.prompt().then(() => {
              setPrompt(null);
            });
          }}
        >
          Установить
        </Button>
      )}
    </div>
  );
}

declare global {
  interface Navigator {
    standalone?: boolean;
  }
}
